/* eslint-disable react/prop-types -- this codebase does not use propTypes (see CLAUDE.md). */
import { useId } from "react";

// Small building blocks shared by the /start screens. Mobile first: 16px text
// in inputs (iOS zooms into anything smaller), 44px+ touch targets, and one
// indigo primary action per screen.

export const BRAND = "#4522ED";

export function cx(...classes) {
  return classes.filter(Boolean).join(" ");
}

export function PrimaryButton({ children, className, loading, disabled, ...props }) {
  return (
    <button
      type="button"
      {...props}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cx(
        "inline-flex min-h-[52px] w-full items-center justify-center gap-2 rounded-xl px-5 text-base font-semibold text-white shadow-sm transition",
        "bg-[#4522ED] hover:bg-[#2F12B8] active:bg-[#2F12B8] disabled:cursor-not-allowed disabled:opacity-60",
        "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#4522ED]",
        className,
      )}
    >
      {loading && <Spinner className="h-5 w-5 text-white" />}
      {children}
    </button>
  );
}

export function SecondaryButton({ children, className, ...props }) {
  return (
    <button
      type="button"
      {...props}
      className={cx(
        "inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-xl border border-gray-300 bg-white px-5 text-base font-semibold text-gray-900 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60",
        "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#4522ED]",
        className,
      )}
    >
      {children}
    </button>
  );
}

export function LinkButton({ children, className, ...props }) {
  return (
    <button
      type="button"
      {...props}
      className={cx(
        "inline-flex min-h-11 items-center justify-center px-2 text-sm font-semibold text-[#4522ED] underline-offset-4 hover:underline disabled:opacity-60",
        className,
      )}
    >
      {children}
    </button>
  );
}

export function Spinner({ className = "h-6 w-6 text-[#4522ED]" }) {
  return (
    <svg className={cx("animate-spin", className)} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="4" />
      <path d="M22 12a10 10 0 0 0-10-10" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
    </svg>
  );
}

export function FullPageSpinner({ label }) {
  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-3" role="status">
      <Spinner />
      {label && <p className="text-sm text-gray-500">{label}</p>}
    </div>
  );
}

export function Section({ title, description, children, id, className }) {
  return (
    <section id={id} className={cx("rounded-2xl border border-gray-200 bg-white p-4 sm:p-5", className)}>
      {title && <h2 className="text-lg font-semibold text-gray-900">{title}</h2>}
      {description && <p className="mt-1 text-sm text-gray-600">{description}</p>}
      <div className={title || description ? "mt-4 space-y-4" : "space-y-4"}>{children}</div>
    </section>
  );
}

export function Field({ label, hint, error, children, htmlFor }) {
  return (
    <div>
      {label && (
        <label htmlFor={htmlFor} className="mb-1.5 block text-sm font-semibold text-gray-800">
          {label}
        </label>
      )}
      {children}
      {hint && !error && <p className="mt-1.5 text-sm text-gray-500">{hint}</p>}
      {error && (
        <p className="mt-1.5 text-sm font-medium text-red-700" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

export const inputClass =
  "block min-h-12 w-full rounded-xl border border-gray-300 bg-white px-3.5 text-base text-gray-900 placeholder:text-gray-400 focus:border-[#4522ED] focus:outline-none focus:ring-2 focus:ring-[#4522ED]/30 aria-[invalid=true]:border-red-500";

export function TextInput({ id, invalid, className, ...props }) {
  return <input id={id} aria-invalid={invalid || undefined} className={cx(inputClass, className)} {...props} />;
}

export function Toggle({ checked, onChange, label, description, disabled, locked }) {
  const id = useId();
  return (
    <div className="flex items-center justify-between gap-4 py-1">
      <div className="min-w-0">
        <span id={`${id}-label`} className="block text-base font-medium text-gray-900">
          {label}
        </span>
        {description && <span className="block text-sm text-gray-500">{description}</span>}
      </div>
      {locked ? (
        <span className="shrink-0 rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-600">
          {locked}
        </span>
      ) : (
        <button
          type="button"
          role="switch"
          aria-checked={!!checked}
          aria-labelledby={`${id}-label`}
          disabled={disabled}
          onClick={() => onChange(!checked)}
          className={cx(
            "relative inline-flex h-8 w-[52px] shrink-0 items-center rounded-full transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#4522ED] disabled:opacity-50",
            checked ? "bg-[#4522ED]" : "bg-gray-300",
          )}
        >
          <span
            aria-hidden="true"
            className={cx(
              "inline-block h-6 w-6 rounded-full bg-white shadow transition",
              checked ? "translate-x-[24px]" : "translate-x-[4px]",
            )}
          />
        </button>
      )}
    </div>
  );
}

export function Chip({ selected, children, className, ...props }) {
  return (
    <button
      type="button"
      aria-pressed={!!selected}
      {...props}
      className={cx(
        "inline-flex min-h-11 shrink-0 items-center justify-center gap-1.5 rounded-full border px-4 text-sm font-semibold transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#4522ED]",
        selected
          ? "border-[#4522ED] bg-[#EEEBFE] text-[#2F12B8]"
          : "border-gray-300 bg-white text-gray-800 hover:bg-gray-50",
        className,
      )}
    >
      {children}
    </button>
  );
}

export function Stepper({ value, min, max, onChange, label, decrementLabel, incrementLabel }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-base font-medium text-gray-900">{label}</span>
      <div className="flex items-center gap-2">
        <button
          type="button"
          aria-label={decrementLabel}
          disabled={value <= min}
          onClick={() => onChange(Math.max(min, value - 1))}
          className="flex h-11 w-11 items-center justify-center rounded-full border border-gray-300 bg-white text-xl font-semibold text-gray-800 disabled:opacity-40"
        >
          −
        </button>
        <span className="w-8 text-center text-lg font-semibold tabular-nums" aria-live="polite">
          {value}
        </span>
        <button
          type="button"
          aria-label={incrementLabel}
          disabled={value >= max}
          onClick={() => onChange(Math.min(max, value + 1))}
          className="flex h-11 w-11 items-center justify-center rounded-full border border-gray-300 bg-white text-xl font-semibold text-gray-800 disabled:opacity-40"
        >
          +
        </button>
      </div>
    </div>
  );
}

export function ErrorBox({ children, onRetry, retryLabel }) {
  return (
    <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800" role="alert">
      <p>{children}</p>
      {onRetry && (
        <button type="button" onClick={onRetry} className="mt-2 min-h-11 font-semibold underline">
          {retryLabel}
        </button>
      )}
    </div>
  );
}

export function InfoBox({ children, tone = "indigo", className }) {
  const tones = {
    indigo: "border-[#DDD9FC] bg-[#F7F6FF] text-[#2F12B8]",
    green: "border-green-200 bg-green-50 text-green-800",
    gray: "border-gray-200 bg-gray-50 text-gray-700",
    amber: "border-amber-200 bg-amber-50 text-amber-900",
  };
  return <div className={cx("rounded-xl border p-4 text-sm", tones[tone], className)}>{children}</div>;
}

// The bottom action area: stays in view on phones without hiding content
// behind it, and respects the iPhone home indicator.
export function StickyFooter({ children }) {
  return (
    <div
      className="sticky bottom-0 z-20 -mx-4 mt-6 border-t border-gray-200 bg-white/95 px-4 pt-3 backdrop-blur sm:-mx-6 sm:px-6"
      style={{ paddingBottom: "max(12px, env(safe-area-inset-bottom))" }}
    >
      {children}
    </div>
  );
}
